import React from 'react';
import {
  Breakpoints,
  Link,
  SupDisclaimer,
  TextAppearance,
  TextCustom,
  useIsMobile,
} from '@vw-marketing/us-components';
import YouTube, { Options } from 'react-youtube';
import {
  getVideoInformation,
  numberMobileTextStyle,
  numberTextStyle,
  trackVideoMilestone,
} from './helpers';
import {
  DisclaimersWrapper,
  StyledLink,
  StyledSlideNumber,
  StyledTextWrapper,
  StyledVideoBody,
  StyledVideoSlideWrapper,
  StyledVideoTitle,
  StyledVideoWrapper,
} from './styles';
import { FeatureAppTrackingEvent } from '../../hooks/use-tracking-manager';

interface VideoSlideProps {
  readonly videoId?: string;
  readonly title?: string;
  readonly body?: string;
  readonly linkText?: string;
  readonly linkUrl?: string;
  readonly disclaimers?: string[];
  readonly slideNumberText?: string;
  readonly onVideoStart?: (videoInfo: FeatureAppTrackingEvent['video']) => void;
  readonly onVideoPlay?: (videoInfo: FeatureAppTrackingEvent['video']) => void;
  readonly onVideoPause?: (videoInfo: FeatureAppTrackingEvent['video']) => void;
  readonly onVideoMilestoneReached?: (
    milestone,
    videoInfo: FeatureAppTrackingEvent['video'],
  ) => void;
}

export const VideoSlide: React.FC<VideoSlideProps> = (props) => {
  const {
    videoId,
    title,
    body,
    linkText,
    linkUrl,
    disclaimers,
    slideNumberText,
    onVideoStart,
    onVideoPlay,
    onVideoPause,
    onVideoMilestoneReached,
  } = props;

  const isMobile = useIsMobile(Breakpoints.b560);
  const viddeoDefaultSettings = {
    width: '1280',
    height: '1000',
    rel: 0,
  } as Options;

  const handleVideoPlayEvent = (e: any) => {
    onVideoPlay && onVideoPlay(getVideoInformation(e));
  };

  const handleVideoPauseEvent = (e: any) => {
    const videoInfoWithPauseReason = {
      ...getVideoInformation(e),
      pauseReason: 'user-click',
    };
    onVideoPause && onVideoPause(videoInfoWithPauseReason);
  };

  const handlePlayerStateChange = (e: any) => {
    if (e?.data === -1) {
      onVideoStart && onVideoStart(getVideoInformation(e));
    }
    onVideoMilestoneReached && trackVideoMilestone(e, onVideoMilestoneReached);
  };

  return (
    <StyledVideoSlideWrapper className="video-slide-wrapper">
      <StyledVideoWrapper className="video-wrapper">
        <YouTube
          containerClassName="youtube-video-container"
          className="youtube-video"
          videoId={videoId}
          opts={viddeoDefaultSettings}
          onPlay={handleVideoPlayEvent}
          onPause={handleVideoPauseEvent}
          onStateChange={handlePlayerStateChange}
        />
        {slideNumberText && (
          <StyledSlideNumber className="video-number">
            <TextCustom
              appearance={TextAppearance.label200}
              style={isMobile ? numberMobileTextStyle : numberTextStyle}
            >
              {slideNumberText}
            </TextCustom>
          </StyledSlideNumber>
        )}
      </StyledVideoWrapper>
      <StyledTextWrapper className="text-wrapper">
        <StyledVideoTitle className="video-title">
          <TextCustom appearance={TextAppearance.headline200}>
            {title}
          </TextCustom>
        </StyledVideoTitle>
        <StyledVideoBody className="video-body">
          <TextCustom appearance={TextAppearance.headline200}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{body}</div>
            {disclaimers && (
              <DisclaimersWrapper>
                {disclaimers.map((disclaimer, index) => (
                  <SupDisclaimer key={index}>{disclaimer}</SupDisclaimer>
                ))}
              </DisclaimersWrapper>
            )}
          </TextCustom>
        </StyledVideoBody>
        <StyledLink className="video-link">
          <Link href={linkUrl} isInline>
            {linkText}
          </Link>
        </StyledLink>
      </StyledTextWrapper>
    </StyledVideoSlideWrapper>
  );
};
